<script setup>
import { ref, computed, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import CrmCalendar from 'dashboard/routes/dashboard/crm/components/CrmCalendar.vue';
import DashboardHomeAPI from 'dashboard/api/dashboardHome';

const { t } = useI18n();
const router = useRouter();
const { accountScopedUrl } = useAccount();

const dialogRef = ref(null);
const isModalOpen = ref(false); // só monta o Dialog quando aberto (evita o <dialog> vazar no body)
const view = ref('month'); // day | month | list
const scope = ref('personal');
const activeFilter = ref('all'); // all | meetings | tasks
const personalAvailable = ref(false);
const sharedAvailable = ref(false);
const hasConnection = ref(true);
const meetings = ref([]);
const tasks = ref([]);
const isLoading = ref(false);

const today = new Date();
const monthRef = ref({ year: today.getFullYear(), month: today.getMonth() });
const currentDay = ref(new Date());

const views = ['day', 'month', 'list'];
const filters = [
  { key: 'all', labelKey: 'ALL' },
  { key: 'meetings', labelKey: 'MEETINGS' },
  { key: 'tasks', labelKey: 'TASKS' },
];
const HOUR_HEIGHT = 44; // px por hora na visão de dia

const showScopeToggle = computed(
  () => personalAvailable.value && sharedAvailable.value
);

const rangeFor = () => {
  if (view.value === 'day') {
    const s = new Date(currentDay.value);
    s.setHours(0, 0, 0, 0);
    const e = new Date(currentDay.value);
    e.setHours(23, 59, 59, 999);
    return { from: s, to: e };
  }
  if (view.value === 'month') {
    const s = new Date(monthRef.value.year, monthRef.value.month, 1);
    s.setDate(s.getDate() - 7);
    const e = new Date(monthRef.value.year, monthRef.value.month + 1, 0);
    e.setDate(e.getDate() + 7);
    return { from: s, to: e };
  }
  const e = new Date();
  e.setDate(e.getDate() + 30);
  return { from: null, to: e }; // lista: sem piso (inclui atrasadas)
};

const reload = async (autoSwitch = false) => {
  const { from, to } = rangeFor();
  isLoading.value = true;
  try {
    const { data } = await DashboardHomeAPI.getAgenda(
      scope.value,
      from ? from.toISOString() : null,
      to ? to.toISOString() : null
    );
    personalAvailable.value = !!data.personal_available;
    sharedAvailable.value = !!data.shared_available;
    hasConnection.value = !!data.has_connection;
    meetings.value = data.meetings || [];
    tasks.value = data.tasks || [];
    if (
      autoSwitch &&
      scope.value === 'personal' &&
      !personalAvailable.value &&
      sharedAvailable.value
    ) {
      scope.value = 'shared';
      await reload(false);
    }
  } catch (error) {
    meetings.value = [];
    tasks.value = [];
  } finally {
    isLoading.value = false;
  }
};

const open = async (initialView = 'month') => {
  view.value = initialView;
  isModalOpen.value = true;
  await nextTick();
  dialogRef.value?.open();
  reload(true);
};
const onDialogClose = () => {
  isModalOpen.value = false;
};
defineExpose({ open });

const setView = v => {
  if (view.value === v) return;
  view.value = v;
  reload();
};
const setScope = s => {
  if (scope.value === s) return;
  scope.value = s;
  reload();
};

const onMonthChange = ({ year, month }) => {
  monthRef.value = { year, month };
  reload();
};

// Clicar num dia no mês → abre a visão de Dia daquele dia.
const onDayClick = dateStr => {
  const [y, m, d] = String(dateStr).split('-').map(Number);
  if (!y || !m || !d) return;
  currentDay.value = new Date(y, m - 1, d);
  view.value = 'day';
  reload();
};

const openGoogleCalendar = () =>
  window.open('https://calendar.google.com/', '_blank', 'noopener');

const shiftDay = delta => {
  const d = new Date(currentDay.value);
  d.setDate(d.getDate() + delta);
  currentDay.value = d;
  reload();
};
const dayToToday = () => {
  currentDay.value = new Date();
  reload();
};

// ── Item helpers ──
const filteredItems = computed(() => {
  if (activeFilter.value === 'tasks') return tasks.value;
  if (activeFilter.value === 'meetings') return meetings.value;
  return [...meetings.value, ...tasks.value];
});

const sortedItems = computed(() =>
  [...filteredItems.value].sort(
    (a, b) => new Date(a.starts_at) - new Date(b.starts_at)
  )
);

// ── List view (grouped by day) ──
const dayKey = iso => new Date(iso).toDateString();
const weekdayLabel = iso => {
  const d = new Date(iso);
  const t0 = new Date();
  const t1 = new Date();
  t1.setDate(t0.getDate() + 1);
  if (d.toDateString() === t0.toDateString()) return t('AGENDA_DROPDOWN.TODAY');
  if (d.toDateString() === t1.toDateString())
    return t('AGENDA_DROPDOWN.TOMORROW');
  return d.toLocaleDateString('pt-BR', {
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
  });
};
const listGroups = computed(() => {
  const map = new Map();
  sortedItems.value.forEach(it => {
    if (!it.starts_at) return;
    const k = dayKey(it.starts_at);
    if (!map.has(k))
      map.set(k, { label: weekdayLabel(it.starts_at), items: [] });
    map.get(k).items.push(it);
  });
  return [...map.values()];
});

const timeLabel = it => {
  if (it.all_day) return t('AGENDA_DROPDOWN.ALL_DAY');
  return new Date(it.starts_at).toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  });
};

// ── Day view ──
const dayLabel = computed(() =>
  currentDay.value.toLocaleDateString('pt-BR', {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
  })
);
const hours = Array.from({ length: 24 }, (_, i) => i);
const hourLabel = h => `${String(h).padStart(2, '0')}:00`;

const isSameDay = (iso, ref0) =>
  new Date(iso).toDateString() === ref0.toDateString();

const dayAllDayItems = computed(() =>
  filteredItems.value.filter(
    it =>
      it.starts_at &&
      isSameDay(it.starts_at, currentDay.value) &&
      (it.all_day || it.kind === 'task')
  )
);

const dayTimedItems = computed(() =>
  filteredItems.value
    .filter(
      it =>
        it.starts_at &&
        !it.all_day &&
        it.kind === 'meeting' &&
        isSameDay(it.starts_at, currentDay.value)
    )
    .map(it => {
      const start = new Date(it.starts_at);
      const end = it.ends_at ? new Date(it.ends_at) : null;
      const startMin = start.getHours() * 60 + start.getMinutes();
      const endMin = end
        ? end.getHours() * 60 + end.getMinutes()
        : startMin + 30;
      const top = (startMin / 60) * HOUR_HEIGHT;
      const height = Math.max(18, ((endMin - startMin) / 60) * HOUR_HEIGHT);
      return { ...it, top, height };
    })
);

const connectUrl = computed(() =>
  accountScopedUrl('settings/integrations/google_calendar')
);

const close = () => dialogRef.value?.close();

const onItemClick = it => {
  if (it.kind === 'task') {
    close();
    router.push(accountScopedUrl('crm/tasks'));
  } else if (it.kind === 'meeting' && it.html_link) {
    window.open(it.html_link, '_blank', 'noopener');
  }
};
</script>

<template>
  <Dialog
    v-if="isModalOpen"
    ref="dialogRef"
    width="3xl"
    :show-cancel-button="false"
    :show-confirm-button="false"
    @close="onDialogClose"
  >
    <div class="flex flex-col gap-3 min-h-[24rem]">
      <!-- Header: title + controls -->
      <div class="flex flex-wrap items-center justify-between gap-2">
        <h3 class="text-base font-semibold text-n-slate-12 m-0">
          {{ t('AGENDA_DROPDOWN.TITLE') }}
        </h3>
        <div class="flex items-center gap-2">
          <!-- scope -->
          <div
            v-if="showScopeToggle"
            class="flex bg-n-alpha-2 rounded-md p-0.5 gap-0.5"
          >
            <button
              v-for="s in ['personal', 'shared']"
              :key="s"
              type="button"
              class="px-2 py-1 text-[11px] font-medium rounded transition-colors"
              :class="
                scope === s
                  ? 'bg-n-solid-2 text-n-slate-12 shadow-sm'
                  : 'text-n-slate-10 hover:text-n-slate-12'
              "
              @click="setScope(s)"
            >
              {{ t(`AGENDA_DROPDOWN.SCOPE_${s.toUpperCase()}`) }}
            </button>
          </div>
          <!-- view toggle -->
          <div class="flex bg-n-alpha-2 rounded-md p-0.5 gap-0.5">
            <button
              v-for="v in views"
              :key="v"
              type="button"
              class="px-2.5 py-1 text-[11px] font-medium rounded transition-colors"
              :class="
                view === v
                  ? 'bg-n-solid-2 text-n-slate-12 shadow-sm'
                  : 'text-n-slate-10 hover:text-n-slate-12'
              "
              @click="setView(v)"
            >
              {{ t(`AGENDA_DROPDOWN.VIEW_${v.toUpperCase()}`) }}
            </button>
          </div>
          <button
            type="button"
            class="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium rounded-md text-n-slate-11 hover:bg-n-alpha-2"
            :title="t('AGENDA_DROPDOWN.OPEN_GOOGLE')"
            @click="openGoogleCalendar"
          >
            <span class="i-lucide-external-link size-3.5" />
            {{ t('AGENDA_DROPDOWN.OPEN_GOOGLE') }}
          </button>
          <button
            type="button"
            class="size-7 rounded-md grid place-content-center text-n-slate-10 hover:bg-n-alpha-2"
            @click="close"
          >
            <span class="i-lucide-x size-4" />
          </button>
        </div>
      </div>

      <!-- filter tabs -->
      <div class="flex items-center gap-1">
        <button
          v-for="f in filters"
          :key="f.key"
          type="button"
          class="px-2.5 py-1 text-[11px] font-medium rounded-md transition-colors"
          :class="
            activeFilter === f.key
              ? 'bg-n-brand/10 text-n-brand'
              : 'text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-1'
          "
          @click="activeFilter = f.key"
        >
          {{ t(`AGENDA_DROPDOWN.FILTER.${f.labelKey}`) }}
        </button>
        <span
          v-if="isLoading"
          class="i-lucide-loader-2 size-3.5 animate-spin text-n-slate-9 ltr:ml-1 rtl:mr-1"
        />
      </div>

      <!-- ── MONTH ── -->
      <div v-if="view === 'month'" class="min-h-[22rem]">
        <CrmCalendar
          :items="filteredItems"
          date-field="starts_at"
          label-field="title"
          :empty-label="t('AGENDA_DROPDOWN.EMPTY')"
          @item-click="onItemClick"
          @month-change="onMonthChange"
          @day-click="onDayClick"
        />
      </div>

      <!-- ── DAY ── -->
      <div v-else-if="view === 'day'" class="flex flex-col gap-2">
        <div class="flex items-center justify-between">
          <span class="text-sm font-medium text-n-slate-12 capitalize">
            {{ dayLabel }}
          </span>
          <div class="flex items-center gap-1">
            <button
              type="button"
              class="size-7 rounded-md grid place-content-center text-n-slate-10 hover:bg-n-alpha-2"
              @click="shiftDay(-1)"
            >
              <span class="i-lucide-chevron-left size-4" />
            </button>
            <button
              type="button"
              class="px-2 py-1 text-[11px] font-medium rounded-md text-n-slate-10 hover:bg-n-alpha-2"
              @click="dayToToday"
            >
              {{ t('AGENDA_DROPDOWN.TODAY_BUTTON') }}
            </button>
            <button
              type="button"
              class="size-7 rounded-md grid place-content-center text-n-slate-10 hover:bg-n-alpha-2"
              @click="shiftDay(1)"
            >
              <span class="i-lucide-chevron-right size-4" />
            </button>
          </div>
        </div>

        <!-- all-day + tasks strip -->
        <div v-if="dayAllDayItems.length" class="flex flex-col gap-1">
          <span class="text-[10px] font-semibold text-n-slate-9 uppercase">
            {{ t('AGENDA_DROPDOWN.ALL_DAY_SECTION') }}
          </span>
          <button
            v-for="it in dayAllDayItems"
            :key="`ad-${it.kind}-${it.id}`"
            type="button"
            class="flex items-center gap-2 px-2 py-1 rounded-md text-left hover:bg-n-alpha-1"
            @click="onItemClick(it)"
          >
            <span
              class="size-2 rounded-full flex-shrink-0"
              :class="it.kind === 'meeting' ? 'bg-n-teal-9' : 'bg-n-brand'"
            />
            <span class="text-xs text-n-slate-12 truncate">{{ it.title }}</span>
          </button>
        </div>

        <!-- hour grid -->
        <div
          class="overflow-y-auto max-h-[24rem] border border-n-weak rounded-lg"
        >
          <div class="relative" :style="{ height: `${24 * HOUR_HEIGHT}px` }">
            <div
              v-for="h in hours"
              :key="h"
              class="absolute left-0 right-0 border-t border-n-weak/60 flex"
              :style="{
                top: `${h * HOUR_HEIGHT}px`,
                height: `${HOUR_HEIGHT}px`,
              }"
            >
              <span
                class="w-12 flex-shrink-0 text-[10px] text-n-slate-9 ltr:text-right rtl:text-left ltr:pr-2 rtl:pl-2 -mt-1.5"
              >
                {{ hourLabel(h) }}
              </span>
            </div>
            <button
              v-for="it in dayTimedItems"
              :key="`ev-${it.id}`"
              type="button"
              class="absolute ltr:left-14 rtl:right-14 ltr:right-2 rtl:left-2 rounded-md bg-n-teal-9/10 px-2 py-0.5 text-left overflow-hidden hover:bg-n-teal-9/20"
              :style="{ top: `${it.top}px`, height: `${it.height}px` }"
              @click="onItemClick(it)"
            >
              <span
                class="block text-[11px] font-medium text-n-slate-12 truncate"
              >
                {{ it.title }}
              </span>
              <span class="block text-[10px] text-n-slate-10">
                {{ timeLabel(it) }}
              </span>
            </button>
          </div>
        </div>
      </div>

      <!-- ── LIST ── -->
      <div v-else class="min-h-[20rem]">
        <div v-if="listGroups.length" class="overflow-y-auto max-h-[28rem]">
          <div v-for="g in listGroups" :key="g.label">
            <div
              class="px-1 py-1.5 text-[11px] font-semibold text-n-slate-10 bg-n-alpha-1 sticky top-0"
            >
              {{ g.label }}
            </div>
            <button
              v-for="it in g.items"
              :key="`${it.kind}-${it.id}`"
              type="button"
              class="flex items-start gap-2.5 w-full px-1 py-2.5 text-left border-b border-n-weak/50 last:border-0 hover:bg-n-slate-2"
              @click="onItemClick(it)"
            >
              <span
                class="mt-0.5 size-6 rounded-md grid place-content-center flex-shrink-0"
                :class="
                  it.kind === 'meeting'
                    ? 'bg-n-teal-9/10 text-n-teal-9'
                    : it.overdue
                      ? 'bg-red-50 text-red-500'
                      : 'bg-n-brand/10 text-n-brand'
                "
              >
                <span
                  class="size-3.5"
                  :class="
                    it.kind === 'meeting'
                      ? 'i-lucide-calendar-clock'
                      : 'i-lucide-circle-check'
                  "
                />
              </span>
              <div class="flex-1 min-w-0">
                <p class="text-xs font-medium text-n-slate-12 m-0 truncate">
                  {{ it.title }}
                </p>
                <span
                  class="text-[11px]"
                  :class="
                    it.overdue ? 'text-red-500 font-semibold' : 'text-n-slate-9'
                  "
                >
                  {{ timeLabel(it) }}
                </span>
              </div>
            </button>
          </div>
        </div>
        <div
          v-else
          class="flex flex-col items-center justify-center py-12 text-center"
        >
          <span class="i-lucide-calendar-check size-6 text-n-slate-8 mb-2" />
          <p class="text-sm text-n-slate-10 m-0">
            {{ t('AGENDA_DROPDOWN.EMPTY') }}
          </p>
        </div>
      </div>

      <!-- connection hint -->
      <RouterLink
        v-if="!hasConnection"
        :to="connectUrl"
        class="flex items-center gap-1.5 text-[11px] text-n-brand hover:underline"
        @click="close"
      >
        <span class="i-lucide-plug size-3.5" />
        {{ t('AGENDA_DROPDOWN.MEETINGS_HINT') }}
      </RouterLink>
    </div>
  </Dialog>
</template>
